<template>
  <loading-button
    v-if="inputLoading"
    :small="small"
    :large="large"
    :full-width="fullWidth"
    :input-btn="inputBtn"
    :full-width-at="fullWidthAt"
  ></loading-button>

  <button
    v-else
    :id="inputId"
    :type="type"
    :disabled="isDisabled"
    v-bind="$attrs"
    :class="[
      buttonTopLevelStyles,
      buttonDisabledBg,
      buttonUppercase,
      buttonTextSize,
      buttonOverflow,
      buttonPadding,
      buttonRadius,
      buttonBorder,
      buttonCursor,
      buttonHeight,
      buttonWidth,
      buttonType,
    ]"
    :tabindex="disabled || noTab ? '-1' : '0'"
    @click="onClick"
  >
    <!-- loader -->
    <base-loading v-show="hasStatus" :status="status || thisStatus" />

    <!-- icon -->
    <template v-if="icon && !hasStatus">
      <base-icon v-if="baseIcon" :icon="icon" class="tw-w-6 tw-h-6" />
      <font-awesome-icon
        v-else
        :class="[Classes.BASE_BUTTON_ICON, iconColor]"
        :icon="icon"
        :size="iconSize"
        fixed-width
      />
    </template>

    <!-- slot data -->
    <span
      v-if="$slots.default && !hasStatus"
      class="tw-transform tw-transition-transform"
      :class="[
        textAnimation,
        { 'tw-w-full': slotFullWidth },
        { 'tw-truncate': truncate },
      ]"
    >
      <slot />
    </span>

    <!-- screen reader text -->
    <span v-if="srOnly" class="sr-only">{{ srOnly }}</span>
  </button>
</template>

<script>
import LoadingButton from '@components/Loading/LoadingButton.vue'
import { genHtmlId, isObjEmpty } from '@helpers/utils.js'
import { useStatus } from '@composables'
import { computed, toRefs } from 'vue'
import Classes from '@config/css.js'
export default {
  components: {
    LoadingButton,
  },
  props: {
    basicBtn: {
      type: Boolean,
      default: false,
    },
    inputBtn: {
      type: Boolean,
      default: false,
    },
    textLink: {
      type: Boolean,
      default: false,
    },
    textLinkHover: {
      type: Boolean,
      default: false,
    },
    primary: {
      type: Boolean,
      default: false,
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    success: {
      type: Boolean,
      default: false,
    },
    info: {
      type: Boolean,
      default: false,
    },
    warning: {
      type: Boolean,
      default: false,
    },
    danger: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    noBg: {
      type: Boolean,
      default: false,
    },
    xLarge: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    xsmall: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    slotFullWidth: {
      type: Boolean,
      default: false,
    },
    autoWidth: {
      type: Boolean,
      default: false,
    },
    fullWidthAt: {
      type: String,
      default: '',
      validator: function (value) {
        return ['xl', 'lg', 'md', 'sm', 'xs', ''].indexOf(value) !== -1
      },
    },
    uppercase: {
      type: Boolean,
      default: false,
    },
    radius: {
      type: String,
      default: 'default',
      validator: function (value) {
        return ['full', 'bottom', 'none', 'default'].indexOf(value) !== -1
      },
    },
    icon: {
      type: [String, Array],
      default: null,
    },
    iconSize: {
      type: String,
      default: null,
    },
    baseIcon: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    status: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'button',
    },
    id: {
      type: String,
      default: null,
    },
    srOnly: {
      type: String,
      default: '',
    },
    noTab: {
      type: Boolean,
      default: false,
    },
    inputLoading: {
      type: Boolean,
      default: false,
    },
    iconColor: {
      type: String,
      default: 'tw-fill-current',
    },
    alignLeft: {
      type: Boolean,
      default: false,
    },
    truncate: {
      type: Boolean,
      default: false,
    },
    noPadding: {
      type: Boolean,
      default: false,
    },
    smallPadding: {
      type: Boolean,
      default: false,
    },
    overflow: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const {
      textLinkHover,
      smallPadding,
      fullWidthAt,
      noPadding,
      alignLeft,
      uppercase,
      fullWidth,
      autoWidth,
      secondary,
      overflow,
      disabled,
      basicBtn,
      inputBtn,
      textLink,
      readOnly,
      warning,
      success,
      primary,
      errors,
      active,
      radius,
      status,
      danger,
      xLarge,
      xsmall,
      large,
      small,
      info,
      icon,
      id,
    } = toRefs(props)

    const inputId = id.value ? id.value : genHtmlId()
    const {
      status: thisStatus,
      statusType,
      watchStatusAnimation,
      updateStatus,
    } = useStatus()

    const { isLoading, isSuccess, isError } = watchStatusAnimation(status)

    const isTextLinkItem = computed(() => textLink.value || textLinkHover.value)

    const isDisabled = computed(
      () => status.value === statusType.LOADING || disabled.value
    )

    const hasStatus = computed(
      () =>
        isLoading.value || isSuccess.value || isError.value || thisStatus.value
    )

    const buttonTopLevelStyles = `tw-group tw-button-transition tw-leading-normal tw-outline-none tw-items-center tw-shrink-0 tw-flex ${
      alignLeft.value ? 'tw-justify-start' : 'tw-justify-center'
    } tw-space-x-2 tw-font-bold`

    const buttonWidth = computed(() => {
      if (fullWidth.value) {
        return 'tw-w-full'
      } else if (basicBtn.value || textLink.value || autoWidth.value) {
        return ''
      } else if (inputBtn.value) {
        if (large.value) {
          return 'tw-input--width-large'
        } else if (small.value) {
          return 'tw-input--width-small'
        }
        return 'tw-input--width'
      } else {
        if (fullWidthAt.value === 'xl') {
          return `tw-w-full xl:tw-w-36`
        } else if (fullWidthAt.value === 'lg') {
          return `tw-w-full lg:tw-w-36`
        } else if (fullWidthAt.value === 'md') {
          return `tw-w-full md:tw-w-36`
        } else if (fullWidthAt.value === 'sm') {
          return `tw-w-full sm:tw-w-36`
        } else if (fullWidthAt.value === 'xs') {
          return `tw-w-full xs:tw-w-36`
        } else {
          return 'tw-w-36'
        }
      }
    })

    const buttonHeight = computed(() => {
      if (xLarge.value && !basicBtn.value) {
        return 'tw-h-16'
      }
      if (large.value && !basicBtn.value) {
        return 'tw-input--height-large'
      } else if (small.value) {
        return 'tw-input--height-small'
      } else if (xsmall.value) {
        return 'tw-input--height-xsmall'
      } else if (basicBtn.value || isTextLinkItem.value) {
        return ''
      } else {
        return 'tw-input--height'
      }
    })

    const buttonTextSize = computed(() => {
      if (large.value) {
        return 'tw-text-md'
      } else if (small.value) {
        return 'tw-text-sm'
      }
      return 'tw-text-sm'
    })

    const buttonCursor = computed(() => {
      if (hasStatus.value || readOnly.value) {
        return 'tw-cursor-default'
      } else if (disabled.value || isDisabled.value) {
        return 'tw-cursor-not-allowed'
      } else {
        return 'tw-cursor-pointer'
      }
    })

    const buttonDisabledBg = computed(() => {
      if (disabled.value) {
        return 'tw-opacity-40'
      }
      return ''
    })

    const buttonType = computed(() => {
      if (textLink.value && !primary.value) return ''
      if (textLink.value)
        return 'game-link-color hover:game-link-color-hover focus:game-link-color-hover'
      if (textLinkHover.value)
        return 'hover:game-link-color focus:game-link-color'

      if (active.value) {
        return _checkStatus(
          'game-active-button-color game-active-text hover:game-active-button-color-hover focus:game-active-button-color-hover hover:game-active-text-hover focus:game-active-text-hover'
        )
      } else if (primary.value) {
        return _checkStatus([
          'game-primary-button-color game-primary-text',
          {
            'hover:game-primary-button-color-hover focus:game-primary-button-color-hover hover:game-primary-text-hover focus:game-primary-text-hover':
              !readOnly.value,
          },
        ])
      } else if (secondary.value) {
        return _checkStatus(
          'game-secondary-button-color game-secondary-text hover:game-secondary-button-color-hover focus:game-secondary-button-color-hover hover:game-secondary-text-hover focus:game-secondary-text-hover'
        )
      } else if (info.value) {
        return _checkStatus(
          'tw-bg-info tw-text-info-text hover:tw-bg-info-hover focus:tw-bg-info-hover'
        )
      } else if (success.value) {
        return _checkStatus(
          'tw-bg-success tw-text-success-text hover:tw-bg-success-hover focus:tw-bg-success-hover'
        )
      } else if (warning.value) {
        return _checkStatus(
          'tw-bg-warning tw-text-warning-text hover:tw-bg-warning-hover focus:tw-bg-warning-hover'
        )
      } else if (danger.value) {
        return _checkStatus(
          'tw-bg-danger tw-text-danger-text hover:tw-bg-danger-hover focus:tw-bg-danger-hover'
        )
      }

      return ''
    })

    const buttonBorder = computed(() => {
      const border = 'tw-border-2'

      if (!textLinkHover.value && !textLink.value) {
        if (active.value)
          return `${border} game-active-button-border-color hover:game-active-button-border-color-hover focus:game-active-button-border-color-hover`
        if (primary.value)
          return [
            border,
            'game-primary-button-border-color',
            {
              'hover:game-primary-button-border-color-hover focus:game-primary-button-border-color-hover':
                !readOnly.value,
            },
          ]
        if (secondary.value)
          return `${border} game-secondary-button-border-color hover:game-secondary-button-border-color-hover focus:game-secondary-button-border-color-hover`
        if (info.value)
          return `${border} tw-border-info hover:tw-border-info-hover focus:tw-border-info-hover`
        if (success.value)
          return `${border} tw-border-success hover:tw-border-success-hover focus:tw-border-success-hover`
        if (warning.value)
          return `${border} tw-border-warning hover:tw-border-warning-hover focus:tw-border-warning-hover`
        if (danger.value)
          return `${border} tw-border-danger hover:tw-border-danger-hover focus:tw-border-danger-hover`
      }

      return `${border} tw-border-transparent`
    })

    const buttonRadius = computed(() => {
      if (radius.value === 'none' || textLinkHover.value) {
        return ''
      } else if (radius.value === 'bottom') {
        return 'tw-global--border-radius-b'
      } else if (radius.value === 'full') {
        return 'tw-rounded-full'
      } else if (radius.value === 'default') {
        return 'game-button-radius'
      } else {
        return 'tw-global--border-radius'
      }
    })

    const buttonUppercase = computed(() =>
      uppercase.value ? 'tw-uppercase' : ''
    )

    const buttonPadding = computed(() =>
      autoWidth.value && noPadding.value
        ? ''
        : smallPadding.value
        ? 'tw-px-1'
        : 'tw-px-3'
    )

    const buttonOverflow = computed(() => ({
      'tw-overflow-hidden': !overflow.value,
    }))

    const textAnimation = computed(() => {
      if (icon.value) {
        return ''
      }
      return hasStatus.value ? 'tw-translate-x-px' : 'tw-translate-x-0'
    })

    function _checkStatus(currentBg) {
      if (hasStatus.value) {
        if (isLoading.value) {
          return currentBg
        } else if (isSuccess.value) {
          return 'tw-bg-success tw-text-success-text tw-border-success active:tw-border-success hover:tw-border-success focus:tw-border-success'
        } else if (isError.value) {
          return 'tw-bg-danger tw-text-danger-text tw-border-danger active:tw-border-danger hover:tw-border-danger focus:tw-border-danger'
        }
      }
      return currentBg
    }

    function onClick() {
      if (errors.value && !isObjEmpty(errors.value)) {
        if (Object.values(errors.value).some((errArray) => errArray.length)) {
          updateStatus(statusType.ERROR)
        }
      }
    }

    return {
      buttonTopLevelStyles,
      buttonDisabledBg,
      buttonUppercase,
      buttonTextSize,
      isTextLinkItem,
      buttonOverflow,
      textAnimation,
      buttonPadding,
      buttonCursor,
      buttonBorder,
      buttonRadius,
      buttonHeight,
      buttonWidth,
      isDisabled,
      buttonType,
      thisStatus,
      hasStatus,
      Classes,
      inputId,
      onClick,
    }
  },
}
</script>
